import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output,
  ViewChild
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatMenuTrigger } from '@angular/material/menu';
import { flatten as _flatten } from 'lodash-es';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  EntityConnectionTypeDetailsFragment, EntityTypeDetailsFragment, GetCampaignByIdNoTagsGQL,
  GetEntityConnectionTypesGQL, GetEntityTypesGQL, TagType
} from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { BatchTagResolverService } from 'field-shared/batch-tag-resolver.service';
import { isNilOrEmpty } from 'helpers';

export type SelectedResponses = { id: string } | Array<{ id: string }>;
export type SelectedType = {
  id: string;
  entityType1: { id: string; namePlural: string };
  entityType2: { id: string; namePlural: string };
} | {
  selectedEntityType: { id: string; namePlural: string };
};

/** Component for selecting responses
 *  with additional filtering by EntityType and "TagCategory"
 */
@Component({
  selector: 'app-advanced-response-selector',
  templateUrl: './advanced-response-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedResponseSelectorComponent extends WithDisposableSubject()
  implements OnInit {

  @Input() hideReadonly: boolean;

  @Input() triggerButtonClass: string | string[] | { [key: string]: boolean };

  /**
   * Optional campaignId. If provided, all options will be limted
   * to only those available in the campaign
   */
  @Input() campaignId;

  @Input() hideUniversal: boolean;

  /**
   * Checkboxes are used if true
   */
  @Input() multiple: boolean;

  /**
   * Sets type of tags for this component
   */
  @Input() targetType: 'EntityType' | 'EntityConnectionType' | 'CalendarEventResponse';

  /** Filter to specific tag types */
  @Input() tagTypes: Array<TagType>;


  /** Limits to only this target type id */
  @Input() targetTypeId: string;

  /** If true, the responses (tags)
   * will be sorted by entity name and then tag name
   */
  @Input() orderByEntityTypeName: boolean;

  /** Currently selected response or responses */
  @Input() selectedResponses: SelectedResponses;

  /**
   * Sets if the component should be used for
   * EntityType tags or EntityConnectionType tags
   */
  @Input() buttonClass: string;


  /** Resets the selected field on close */
  @Input() resetSelectedFieldOnClose: boolean;

  /** Resets the selected type on close */
  @Input() resetSelectedTypeOnClose: boolean;

  /** Outputs the selected response(s)
   * if they are changed
   */
  @Output() selectedResponsesChange = new EventEmitter<SelectedResponses>();

  /**
   * Determines if the select all is shown
   * true by default
   */
  @Input() allowSelectAll = true;

  selectAllLoading = false;

  // <View Model Variables>
  typeOptions: EntityTypeDetailsFragment[] | EntityConnectionTypeDetailsFragment[] = [];

  selectedType: SelectedType;

  /** The selected field from the dropdown (if any) */
  selectedField: { id: string };

  get emptyTypeTriggerText() {
    if (this.targetType === 'EntityConnectionType') {
      return 'All Connection Types ';
    }
    return 'All Entity Types';
  }

  get getTriggerText() {
    const selectedResponses = this._selectedResponses;
    if (isNilOrEmpty(selectedResponses)) {
      return 'Select Info';
    };

    if (this.multiple) {
      if (selectedResponses.length === 1) {
        return `${selectedResponses?.length} response selected`;
      }
      return `${selectedResponses?.length} responses selected`;
    }

    // TODO: Label for single select
    return 'Select Info';
  }


  /** Pass to app-response-search to filter the results based upon the selected field */
  get selectedFieldIds() {
    return this.selectedField ? [this.selectedField.id] : null;
  }

  get hasSelectedResponses() {
    return !isNilOrEmpty(this.selectedResponses);
  }

  /**
   * Returns the selectedResponses input as an array even if input
   * is a single response.
   */
  get _selectedResponses(): { id: string }[] {
    if (isNilOrEmpty(this.selectedResponses)) {
      return [];
    } else {
      return _flatten([this.selectedResponses].filter(r => r));
    }
  }

  // </View Model Variables>

  /** Is the overlay for the select open? */
  isOpen = false;

  /** Reference to trigger to open the select
   *  (Used to dynamically set initial width)
   */
  @ViewChild(CdkOverlayOrigin) trigger: CdkOverlayOrigin;

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  private _triggerWidth = new Subject<string>();

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  triggerWidth$ = this._triggerWidth.asObservable().pipe(
    takeUntil(this.disposer)
  );

  /**
   *
   */
  constructor(
    private _getCampaignNoTagsGQL: GetCampaignByIdNoTagsGQL,
    private _getEntityTypesGQL: GetEntityTypesGQL,
    private _getEntityConnectionTypesGQL: GetEntityConnectionTypesGQL,
    private _cd: ChangeDetectorRef,
    private _batchTagResolver: BatchTagResolverService
  ) {
    super();
  }

  ngOnInit() {
    // Set entity type options
    this._getOptions(this.campaignId).subscribe(
      (opts) => {
        if (this.targetType === 'EntityConnectionType') {
          this.typeOptions = opts.entityConnectionTypes;
        } else {
          this.typeOptions = opts.entityTypes;
        }

        if (this.targetTypeId) {
          const st = (this.typeOptions as Array<any>).find((t: any) => t.id === this.targetTypeId);
          this.onSelectedTypedChanged(st);
        }
      }
    );
  }

  private _getOptions(campaignId: string) {
    // Case where a campaignId is provided
    if (campaignId) {
      return this._getCampaignNoTagsGQL.fetch({ campaignId: this.campaignId }).pipe(
        map(({ data }) => ({
          entityTypes: data?.getCampaign.entityTypes,
          entityConnectionTypes: data?.getCampaign.entityConnectionTypes
        }))
      );
    } else {
      const ets$ = this._getEntityTypesGQL.fetch().pipe(
        map(r => r.data.getEntityTypes.nodes)
      );
      const ects$ = this._getEntityConnectionTypesGQL.fetch().pipe(
        map(r => r.data.getEntityConnectionTypes)
      );

      return combineLatest([ets$, ects$]).pipe(
        map(([entityTypes, entityConnectionTypes]) =>
          ({ entityTypes, entityConnectionTypes })
        )
      );
    }
  }

  /**
   * Sets the currently selected entity type
   *
   * @param entityType entity type
   */
  onSelectedTypedChanged(
    type?: SelectedType,
    matMenuTrigger?: MatMenuTrigger
  ) {
    if (this.selectedType !== type) {
      this.selectedField = null;
    }
    this.selectedType = type;
    this._cd.markForCheck();
    matMenuTrigger?.closeMenu();
  }

  /**
   * Triggered when the selected field has changed in the dropdown
   *
   * @param field TagCategory
   */
  selectedFieldChanged(field: any) {
    this.selectedField = field;
  }

  /**
   * Return true if the response is currently selected
   *
   * @param id Id of the response
   */
  isResponseSelected(id: string) {
    return this._selectedResponses.map(i => i.id).includes(id);
  }

  /**
   * Used for single select template
   */
  onResponseSelected(response: { id: string }) {
    this.selectedResponsesChange.emit(response);
    if (!this.multiple) {
      this.isOpen = false;
    }
  }

  /**
   * Used for multiselect template
   *
   * @param event checkbox change event
   * @param response response item changed
   */
  onResponseCheckboxChanged(event: MatCheckboxChange, response: any) {
    if (event.checked && !this.isResponseSelected(response?.id)) {
      return this.addResponse(response);
    }
    if (event.checked === false) {
      return this.removeResponse(response);
    }
  }

  onAllTagsSelected(ids: string[]) {
    if (ids?.length) {
      const newIds = ids.filter(id => !this.isResponseSelected(id));
      this._batchTagResolver.getTags(newIds).subscribe(
        (tags) => {
          this.selectedResponsesChange.emit([...tags, ...this._selectedResponses]);
        },
        (e) => e,
        () => {
          this.selectAllLoading = false;
          this.closeOverlay();
        }
      );
    } else {
      this.selectedResponsesChange.emit([]);
      this.selectAllLoading = false;
      this.closeOverlay();
    }
  }

  private addResponse(response: { id: string }) {
    const updatedResponses = [response, ...this._selectedResponses];
    this.selectedResponsesChange.emit(updatedResponses);
  }

  private removeResponse(response: { id: string }) {
    const updatedResponses = this._selectedResponses.filter(r => r?.id !== response.id);
    this.selectedResponsesChange.emit(updatedResponses);
  }

  openOverlay() {
    const width = this.trigger?.elementRef?.nativeElement?.offsetWidth;
    this._triggerWidth.next(width ? `${width}px` : '100%');
    this.isOpen = true;
    this._cd.markForCheck();
  }

  closeOverlay() {
    this.isOpen = false;
    this.onOverlayClosed();
    this._cd.markForCheck();
  }

  onOverlayClosed() {
    if (this.resetSelectedTypeOnClose) {
      return this.onSelectedTypedChanged(null);
    }
    if (this.resetSelectedFieldOnClose) {
      this.selectedFieldChanged(null);
    }
  }

}
